import type { OrchestratedQueryStore } from "@/domains/library"
import { IEventBus } from "@/domains/events"
import { OrchestratedQuery } from "@/domains/library"
import { OnChainService } from "@/domains/on-chain"
import { WalletConnectionStore } from "@/domains/wallet"

import type { TokenBlacklistChecker } from "./tokens-blacklist"
import type { TokenApiDAO, TokenApiResponse } from "./types"
import { TokenApiTransport } from "./token-api-transport"
import { TokenOnChainTransport } from "./token-onchain-transport"
import { TokenOrchestrator } from "./token-orchestrator"
import { TokenRepository } from "./token-repository"

export class TokenOrchestratorFactory {
  /**
   * Create a fully configured TokenOrchestrator with its OrchestratedQuery
   */
  static create(
    tokenApiTransport: TokenApiTransport,
    tokenOnChainTransport: TokenOnChainTransport,
    tokenRepository: TokenRepository,
    orchestratedQueryStore: OrchestratedQueryStore,
    walletStore: WalletConnectionStore,
    blacklistChecker: TokenBlacklistChecker,
    eventBus: IEventBus,
  ): TokenOrchestrator {
    // 1. Create orchestrator with both transports
    const orchestrator = new TokenOrchestrator(
      tokenRepository,
      walletStore,
      tokenOnChainTransport,
      blacklistChecker,
      eventBus,
    )

    // 2. Create token sync query with orchestrator's callbacks
    const tokenSyncQuery = new OrchestratedQuery<Record<string, TokenApiDAO>>(
      "Token Sync",
      (_query) => tokenApiTransport.loadTokens(), // Query parameter available but not used (no chunking)
      60_000, // 60 seconds default interval
      orchestrator.onLoadTokenQuerySuccess.bind(orchestrator),
      orchestrator.onLoadTokenQueryFailed.bind(orchestrator),
      orchestratedQueryStore, // Auto-registers itself
    )

    // 3. Create balance sync query (starts in stopped state) with chunk size configuration
    const balanceSyncQuery = new OrchestratedQuery<Map<string, bigint>>(
      "Token Balance Sync",
      (query) => orchestrator.onUpdateBalancesTrigger(query),
      30_000,
      orchestrator.onLoadBalanceQuerySuccess.bind(orchestrator),
      orchestrator.onLoadBalanceQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
      undefined, // rateLimit
      { min: 1, max: 200, default: 50 }, // chunkSizeBounds
    )

    // 4. Create native balance query (starts in stopped state)
    const nativeBalanceQuery = new OrchestratedQuery<bigint>(
      "Native Balance Sync",
      (query) => orchestrator.onUpdateNativeBalanceTrigger(query),
      60_000, // 60 seconds interval
      orchestrator.onLoadNativeBalanceQuerySuccess.bind(orchestrator),
      orchestrator.onLoadNativeBalanceQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    orchestrator.loadStaticTokens()

    // 5. Initialize orchestrator with all queries
    orchestrator.initialize(tokenSyncQuery, balanceSyncQuery, nativeBalanceQuery)

    // 6. Start token sync query immediately (loads tokens from API)
    tokenSyncQuery.start()

    return orchestrator
  }
}
